import { type DragEvent, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../../lib/cn';

const ACCEPTED_MIME = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
]);
const MAX_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB

export interface DocumentPayload {
  filename: string;
  mime: string;
  dataBase64: string;
}

interface Props {
  disabled?: boolean;
  onDocument: (doc: DocumentPayload) => void;
}

export function NovaChatComposer({ disabled, onDocument }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function processFile(file: File) {
    setError(null);

    // Reject .docx by extension or MIME
    if (
      file.name.toLowerCase().endsWith('.docx') ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      setError("Format non géré. Convertissez votre document en PDF.");
      return;
    }

    if (!ACCEPTED_MIME.has(file.type)) {
      setError("Format non géré. Seuls les PDF et images (PNG, JPEG, WebP) sont acceptés.");
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setError("Fichier trop volumineux (15 Mo maximum).");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip "data:<mime>;base64," prefix
      const base64 = result.split(',')[1] ?? '';
      if (!base64) {
        setError("Impossible de lire le fichier.");
        return;
      }
      onDocument({ filename: file.name, mime: file.type, dataBase64: base64 });
    };
    reader.onerror = () => setError("Impossible de lire le fichier.");
    reader.readAsDataURL(file);
  }

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    if (disabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    if (disabled) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    // Reset so same file can be re-selected
    e.target.value = '';
  }

  return (
    <div className="p-3">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Zone de dépôt de document PDF ou image"
        aria-disabled={disabled}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!disabled) inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition',
          dragOver && !disabled
            ? 'border-cime-primary bg-cime-primary/5'
            : 'border-neutral-200 bg-neutral-50 hover:border-cime-primary/40',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        )}
      >
        <Upload className="w-7 h-7 text-cime-text-muted" />
        <p className="text-sm font-medium text-cime-text-primary">
          Glissez un document ici
        </p>
        <p className="text-xs text-cime-text-muted">
          PDF ou image — 15 Mo max
        </p>
        {!disabled && (
          <span className="text-xs text-cime-primary underline">
            Ou cliquez pour parcourir
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleInputChange}
        tabIndex={-1}
      />

      {error && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
